import * as React from "react";
import { useTranslation } from "react-i18next";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { auth } from "../config/firebase";
import { useGetUserCredits } from "@dataconnect/generated/react";
import { UserStatus } from "@dataconnect/generated";

const CheckoutSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Poll for user status every 2 seconds using DataConnect hook
  const { data } = useGetUserCredits({
    enabled: !!auth.currentUser,
    refetchInterval: 2000, // Poll every 2 seconds
  });

  const userData = data?.user;

  console.log(userData);

  React.useEffect(() => {
    if (userData?.status === UserStatus.ACTIVE) {
      // User is now active, redirect to home
      navigate({ to: "/", search: { returnTab: undefined } });
    }
  }, [userData?.status, navigate]);

  return (
    <div className="h-full w-full overflow-y-auto bg-gray-50">
      <div className="flex min-h-full w-full items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            {/* Success Icon */}
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-8 w-8 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>

            {/* Title */}
            <h1 className="mb-3 text-2xl tracking-tight font-semibold text-gray-900">
              {t("checkout.success.title", "Thank you for subscribing!")}
            </h1>

            {/* Description */}
            <p className="mb-6 text-gray-700">
              {t(
                "checkout.success.description",
                "We're processing your payment. This usually takes just a few seconds."
              )}
            </p>

            {/* Loading indicator */}
            <div className="flex flex-col items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <svg
                  className="h-5 w-5 animate-spin text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>
                  {t(
                    "checkout.success.waiting",
                    "Waiting for payment confirmation..."
                  )}
                </span>
              </div>

              {/* Status badge */}
              <div className="mt-2 inline-flex items-center gap-2 rounded-full bg-blue-50 px-4 py-2 text-sm">
                <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse" />
                <span className="font-medium text-blue-700">
                  {userData?.status === UserStatus.TRIAL
                    ? t("checkout.success.statusTrial", "Trial")
                    : userData?.status || t("common.loading", "Loading...")}
                </span>
              </div>
            </div>

            {/* Help text */}
            <div className="mt-8 rounded-xl bg-gray-50 p-4 text-left">
              <p className="text-xs text-gray-700">
                {t(
                  "checkout.success.helpText",
                  "If this takes longer than expected, you can close this page and come back later. Your subscription will be activated automatically once the payment is confirmed."
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/checkout-success")({
  component: CheckoutSuccessPage,
});
